import { Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DadosFamiliaUsrSPM } from '../../../../../interfaces/entities/Cancelamento/PassoSelecioneBeneficiarios/DadosFamiliaUserSPM';
import { formatISOFormatToDate } from '../../../../../utils/DateFormat';
import './styles.css';
import { sxTable } from './sxProps';
import { BenefsToCancelTableProps } from '../../../../../interfaces/props/Cancelamento/BenefsToCancelTableProps';
import { useAccessibility } from '../../../../../Store/contexts/AccessibilityContext';
import { useRef } from 'react';

const BenefsToCancelTable: React.FC<BenefsToCancelTableProps> = ({ data, onBenefChange }) => {

  const allBenefsChecked: boolean = (data?.lstDadosFamiliaUsrSPM.every((familiar) => (familiar.checked)) || false);

  const { textStyleAccessibility, contrastStyle, contrastBorderAccessibility, contrastColorAccessibility } = useAccessibility();


  const checkboxRefs = useRef<(HTMLInputElement | null)[]>([]);
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    ...{
      fontFamily: 'Unimed-Sans',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 400,
    },
    ...textStyleAccessibility
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: '#F9F9F9'
    },
  }));

  const toggleCheckValue = (index: number, familiar: DadosFamiliaUsrSPM, shouldFocus: boolean = true) => {
    let checked = familiar.checked;
    familiar.checked = !checked;
    onBenefChange(index, familiar)

    if (shouldFocus) {
      setTimeout(() => {
        const nextEnabledIndex = data?.lstDadosFamiliaUsrSPM.findIndex((benef, i) => i > index && benef.enable);
        if (typeof nextEnabledIndex === 'number' && nextEnabledIndex !== -1) {
          checkboxRefs.current[nextEnabledIndex]?.focus();
        }
      }, 0);
    }
  }

  const toggleCheckAllBenefs = () => {
    const toggledCheckedState = !allBenefsChecked;
    data?.lstDadosFamiliaUsrSPM.forEach((familiar, index) => {
      if (familiar.checked !== toggledCheckedState && familiar.enable) {
        toggleCheckValue(index, familiar);
      }
    });
  };

  const getTipoUsrText = (codTipoUsr: string) => {
    switch (codTipoUsr) {
      case 'TITULA':
        return 'TITULAR';
      case 'DEPEND':
        return 'DEPENDENTE';
      case 'AGREGA':
        return 'AGREGADO';
      default:
        return '-'
    }
  }

  const getRowAriaLabel = (row: DadosFamiliaUsrSPM) => {
    const estadoSelecao = row.checked ? 'Selecionado' : 'Não selecionado';
    return `${estadoSelecao}, ${row.contrato.nome}, Cartão ${row.contrato.codAntigoUsr}, Status ${row.contrato.descrStatusUsr}, Tipo ${getTipoUsrText(row.contrato.codTipoUsr)}, Inclusão ${formatISOFormatToDate(row.contrato.dtIncl)}, Exclusão ${row.contrato.dtExcl ? formatISOFormatToDate(row.contrato.dtExcl) : 'não informada'}`;
  };

  const handleTableWrapperFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    if (e.target === tableWrapperRef.current) {
      setTimeout(() => {
        selectAllRef.current?.focus();
      }, 0);
    }
  };

  return (
    <div
      ref={tableWrapperRef}
      tabIndex={0}
      onFocus={handleTableWrapperFocus}
      style={{ outline: 'none' }}
    >
      <TableContainer className='container-table' style={{ ...contrastBorderAccessibility, ...contrastStyle }} sx={sxTable} component={Paper}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  style={contrastColorAccessibility}
                  indeterminate={false}
                  checked={allBenefsChecked}
                  onChange={() => {
                    toggleCheckAllBenefs();
                    setTimeout(() => {
                      selectAllRef.current?.blur();
                    }, 0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      const firstEnabledIndex = data?.lstDadosFamiliaUsrSPM.findIndex(benef => benef.enable);
                      if (typeof firstEnabledIndex === 'number' && firstEnabledIndex !== -1) {
                        checkboxRefs.current[firstEnabledIndex]?.focus();
                      }
                    }
                  }}
                  inputProps={{
                    'aria-label': 'Selecionar todos os beneficiários',
                    tabIndex: -1,
                    role: 'checkbox'
                  }}
                />
              </StyledTableCell>
              <StyledTableCell style={textStyleAccessibility}><b>Nome</b></StyledTableCell>
              <StyledTableCell align="left"><b>Cartão Unimed</b></StyledTableCell>
              <StyledTableCell align="left"><b>Status</b></StyledTableCell>
              <StyledTableCell align="left"><b>Tipo</b></StyledTableCell>
              <StyledTableCell align="left"><b>Inclusão</b></StyledTableCell>
              <StyledTableCell align="left"><b>Exclusão</b></StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.lstDadosFamiliaUsrSPM.map((row, index) => (
              <StyledTableRow
                key={index}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <StyledTableCell padding="checkbox">
                  <Checkbox
                    inputRef={(el) => (checkboxRefs.current[index] = el)}
                    color="primary"
                    style={contrastColorAccessibility}
                    checked={row.checked}
                    disabled={!row.enable}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        const nextIndex = index + 1;
                        if (nextIndex < checkboxRefs.current.length) {
                          checkboxRefs.current[nextIndex]?.focus();
                        }
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        if (index === 0) {
                          selectAllRef.current?.focus();
                        } else {
                          checkboxRefs.current[index - 1]?.focus();
                        }
                      }
                    }}
                    onChange={() => toggleCheckValue(index, row)}
                    inputProps={{
                      'aria-label': getRowAriaLabel(row),
                      'aria-checked': row.checked
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell component="th" scope="row">
                  {row.contrato.nome}
                </StyledTableCell>
                <StyledTableCell align="left">{row.contrato.codAntigoUsr}</StyledTableCell>
                <StyledTableCell align="left">{row.contrato.descrStatusUsr}</StyledTableCell>
                <StyledTableCell align="left">{getTipoUsrText(row.contrato.codTipoUsr)}</StyledTableCell>
                <StyledTableCell align="left">{formatISOFormatToDate(row.contrato.dtIncl)}</StyledTableCell>
                <StyledTableCell align="left">{row.contrato.dtExcl ? (formatISOFormatToDate(row.contrato.dtExcl)) : '-'}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default BenefsToCancelTable;